import { Survey } from '../hooks/useSurveyData';

export interface Question {
  id: string;
  number: number;
  text: string;
  fullLabel?: string; // Full label text for parsing scale info
  type: 'rating' | 'radio' | 'checkbox' | 'textfield' | 'done';
  options?: string[];
}

// Helper function to parse question text from node label
export const parseQuestionText = (label?: string): string => {
  if (!label) return '';
  const lines = label.split('\n');
  const firstLine = lines[0] || '';
  const match = firstLine.match(/^Q\d+(?:\.\d+)?:\s*(.+)$/);
  if (match) {
    return match[1].trim();
  }
  return firstLine.replace(/^Q\d+(?:\.\d+)?:\s*/, '').trim();
};

// Helper function to determine question type
export const determineQuestionType = (node: any): 'rating' | 'radio' | 'checkbox' | 'textfield' | 'done' => {
  const meta = node.data?.meta;
  if (meta?.type) {
    return meta.type;
  }
  if (node.type === 'output' || node.id === 'end') {
    return 'done';
  }
  const label = node.data?.label || '';
  if (label.toLowerCase().includes('done')) {
    return 'done';
  }
  if (label.toLowerCase().includes('scale:')) {
    return 'rating';
  }
  if (label.toLowerCase().includes('options:') || label.toLowerCase().includes('choices:')) {
    const optionLine = label.split('\n').find((line: string) => 
      line.trim().toLowerCase().startsWith('options:') || line.trim().toLowerCase().startsWith('choices:')
    );
    if (optionLine?.toLowerCase().startsWith('choices:')) {
      return 'checkbox';
    }
    return 'radio';
  }
  return 'textfield';
};

// Helper function to extract options from label
export const extractOptionsFromLabel = (label: string): string[] => {
  const lines = label.split('\n');
  const optionLine = lines.find((line) => 
    line.trim().toLowerCase().startsWith('options:') || line.trim().toLowerCase().startsWith('choices:')
  );
  if (!optionLine) return [];
  const list = optionLine.split(':')[1]?.trim() || '';
  return list.split(',').map((item) => item.trim()).filter(Boolean);
};

/** Check if survey.questions is card-v1 format (has format and questions array) */
function isCardV1Questions(q: unknown): q is { format: string; questions: unknown[]; paths?: unknown[] } {
  return (
    q != null &&
    typeof q === 'object' &&
    'format' in q &&
    (q as { format: string }).format === 'card-v1' &&
    'questions' in q &&
    Array.isArray((q as { questions: unknown[] }).questions)
  );
}

/** Extract questions from card-v1 survey (questions array; exclude done steps) */
function extractQuestionsFromCardV1(cardQuestions: unknown[]): Question[] {
  const toNum = (n: number | string): number => {
    if (typeof n === 'number') return Number.isFinite(n) ? n : 0;
    const parsed = parseFloat(String(n));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const questions: Question[] = [];
  for (const q of cardQuestions) {
    const card = q as { id?: string; number?: number | string; text?: string; type?: string; config?: { options?: string[]; format?: string; rateAmount?: number } };
    if (!card || card.type === 'done') continue;

    const num = card.number != null ? toNum(card.number) : 0;
    if (num <= 0) continue;

    const type = (card.type === 'rating' || card.type === 'radio' || card.type === 'checkbox' || card.type === 'textfield' || card.type === 'done')
      ? card.type
      : 'textfield';
    const text = typeof card.text === 'string' ? card.text : `Question ${num}`;
    const options = Array.isArray(card.config?.options) ? card.config.options : undefined;

    let fullLabel = text;
    if (type === 'rating' && card.config?.rateAmount) {
      fullLabel += ` scale: numerical (${card.config.rateAmount})`;
    } else if (options && options.length > 0) {
      fullLabel += ` options: ${options.join(', ')}`;
    }

    questions.push({
      id: typeof card.id === 'string' ? card.id : `q-${num}`,
      number: num,
      text,
      fullLabel,
      type,
      options,
    });
  }
  return questions.sort((a, b) => a.number - b.number);
}

// Extract questions from survey (card-v1 or legacy nodes/edges)
export const extractQuestions = (survey: Survey | null): Question[] => {
  const q = survey?.questions;
  if (!q || typeof q !== 'object') return [];

  // New format: card-v1 with questions[] array
  if (isCardV1Questions(q)) {
    return extractQuestionsFromCardV1(q.questions);
  }

  // Legacy format: nodes/edges
  if (!('nodes' in q) || !Array.isArray((q as { nodes: unknown[] }).nodes)) return [];

  const questions: Question[] = [];
  const nodes = (q as { nodes: any[] }).nodes;
  const edges = (q as { edges?: any[] }).edges || [];
  
  // Build edge map to find main path
  const outgoingEdges = new Map<string, any[]>();
  edges.forEach((edge: { source: string; target: string; data?: { conditional?: boolean } }) => {
    if (!outgoingEdges.has(edge.source)) {
      outgoingEdges.set(edge.source, []);
    }
    outgoingEdges.get(edge.source)!.push(edge);
  });
  
  // Find main path from start to end
  const findMainPath = (): string[] => {
    const path: string[] = ['start'];
    const visited = new Set<string>(['start']);
    let current = 'start';
    
    while (current !== 'end') {
      const edges = outgoingEdges.get(current) || [];
      const nonConditional = edges.filter((e) => !e.data?.conditional);
      const nextEdge = nonConditional.length > 0 ? nonConditional[0] : edges[0];
      
      if (!nextEdge || visited.has(nextEdge.target)) {
        break;
      }
      
      current = nextEdge.target;
      path.push(current);
      visited.add(current);
    }
    
    return path;
  };
  
  const mainPath = findMainPath();
  let questionNumber = 1;
  
  for (let i = 1; i < mainPath.length - 1; i++) {
    const nodeId = mainPath[i];
    const node = nodes.find((n: { id: string; data?: { label?: string } }) => n.id === nodeId);
    if (!node) continue;
    
    const questionType = determineQuestionType(node);
    if (questionType === 'done') continue;
    
    const label = (node as { data?: { label?: string } }).data?.label || '';
    const questionText = parseQuestionText(label) || `Question ${questionNumber}`;
    const options = extractOptionsFromLabel(label);
    
    // Extract question number from label
    const labelMatch = label.match(/^Q(\d+(?:\.\d+)?):/);
    const num = labelMatch ? parseFloat(labelMatch[1]) : questionNumber;
    
    questions.push({
      id: nodeId,
      number: num,
      text: questionText,
      fullLabel: label, // Store full label for scale parsing
      type: questionType,
      options: options.length > 0 ? options : undefined,
    });
    
    questionNumber++;
  }
  
  const toNum = (n: number | string): number => {
    if (typeof n === 'number') return Number.isFinite(n) ? n : 0;
    const parsed = parseFloat(String(n));
    return Number.isFinite(parsed) ? parsed : 0;
  };
  return questions.sort((a, b) => toNum(a.number) - toNum(b.number));
};

